import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, InjectionToken, input } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ButtonComponent, CommaPipe, DashedButtonDirective } from '@ui/aos-lib';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { TranslateModule } from '@ngx-translate/core';
import { DevicesDataService } from 'src/app/services/data/devices-data/devices-data.service';
import { ExportRow } from '@portal/shared/types/export.types';
import { DeviceListingFieldName, DevicesPayload } from 'src/app/pages/equipment/devices/components/devices.models';
import { map, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import {
  selectColumnsOrder,
  selectColumnsVisibility
} from '@portal/shared/components/listing-page/store/listing-page.reducer';
import { ListingPageService } from '@portal/shared/components/listing-page/listing-page.service';
import { ListingPageId } from '@portal/shared/components/listing-page/store/listing-page-store.types';
import { ExportFacadeService, ExportFileType } from 'src/app/services/utility/export-facade.service';

export interface LassoDynamicFilterCommand {
  execute(): void;
}

export const lassoDynamicFilterCommandToken = new InjectionToken<LassoDynamicFilterCommand>(
  'lassoDynamicFilterCommandToken'
);

@Component({
  selector: 'aos-lasso-dynamic-filter',
  imports: [ReactiveFormsModule, ButtonComponent, TranslateModule, DashedButtonDirective, CommaPipe],
  template: `
    <form
      class="aos-dynamic-filters"
      [formGroup]="form()"
    >
      @let formControl = form().get('values');
      @let selectedDevices = formControl?.value;
      @if (selectedDevices?.length) {
        <div>
          <span> {{ selectedDevices.length | comma }} {{ devicesSelectedLabel | translate }} </span>
          <span
            [uiDashedButton]="exportCSVLabel | translate"
            (elementClicked)="export(selectedDevices)"
          >
            ({{ exportCSVLabel | translate }})
          </span>
        </div>
      } @else {
        <div class="aos-dynamic-filters__selected-devices">{{ noDevicesSelectedLabel | translate }}</div>
      }
    </form>
    <ui-button
      themeColor="secondary"
      (click)="lassoDynamicFilterCommandService.execute()"
    >
      {{ selectOnMapLabel | translate }}
    </ui-button>
  `,
  styleUrl: './dynamic-filters.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LassoDynamicFilterComponent {
  form = input.required<FormGroup>();

  protected readonly lassoDynamicFilterCommandService = inject(lassoDynamicFilterCommandToken);
  protected readonly devicesDataService = inject(DevicesDataService);
  protected readonly pageState = inject(ListingPageService);
  protected readonly exportFacadeService = inject(ExportFacadeService);

  readonly #destroyRef = inject(DestroyRef);
  readonly #store = inject(Store);

  protected readonly columnsVisibility = computed<Record<string, boolean> | null>(() =>
    this.#store.selectSignal(selectColumnsVisibility((this.pageState.model?.pageId ?? '') as ListingPageId))()
  );
  protected readonly columnsOrder = computed<Record<string, number> | null>(() =>
    this.#store.selectSignal(selectColumnsOrder((this.pageState.model?.pageId ?? '') as ListingPageId))()
  );
  protected devicesSelectedLabel = TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.HINT.DEVICES_SELECTED;
  protected noDevicesSelectedLabel = TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.HINT.NO_DEVICES_SELECTED;
  protected selectOnMapLabel = TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.HINT.SELECT_ON_MAP;
  protected exportCSVLabel = TranslateRegistry.get.EVENTS_MONITOR_LISTING_TABLE.BUTTON.EXPORT_CSV;

  protected export(selectedDevices: string[]): void {
    const payload: Pick<DevicesPayload, 'filters' | 'sort'> = {
      filters: [
        {
          filterType: DeviceListingFieldName.DeviceUID,
          filteredValues: selectedDevices
        }
      ]
    };
    this.devicesDataService
      .export(payload)
      .pipe(
        take(1),
        map(response =>
          response.map(
            device =>
              ({
                commDeviceId: { title: 'CommDeviceID', value: device.commDeviceId ?? device.parentCommDeviceId ?? '' },
                serialNumber: { title: 'SerialNumber', value: device.serialNumber ?? '' },
                deviceType: { title: 'DeviceType', value: device.deviceType },
                accountNumber: {
                  title: 'AccountNumber',
                  value:
                    device.accountNumber && device.accountSubNumber
                      ? `${device.accountNumber} / ${device.accountSubNumber}`
                      : ''
                },
                serviceLocationIds: {
                  title: 'ServiceLocationNumber',
                  value: device.serviceLocationIds
                },
                latitude: { title: 'Latitude', value: device.geoMetaData?.latitude },
                longitude: { title: 'Longitude', value: device.geoMetaData?.longitude },
                serviceLocationAddress: { title: 'ServiceLocationAddress', value: device.serviceLocationAddress }
              }) as ExportRow
          )
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(data =>
        this.exportFacadeService.exportRows(
          ExportFileType.CSV,
          data,
          this.columnsVisibility(),
          this.columnsOrder(),
          'Export-Devices'
        )
      );
  }
}
